import React, { useEffect, useMemo, useState } from "react";
import { Button, Glyphicon, FormGroup, ControlLabel, FormControl, Checkbox, Table, Radio, Modal, ButtonGroup } from "react-bootstrap";
import Select from "react-select";
import { bitrateOptions, defaultLanguages, transcoderPresets } from "./lib";
import { useUserContext } from "../../../../../context";
import { addLanguage, deleteLanguage, getLanguages, updateLanguage } from "../../../../../../api/cp/language";
import DeleteConfirmOverlay from "../../../../UI/DeleteConfirmOverlay";
import cloneDeep from "lodash.clonedeep";
import { QUALITIES_DEFAULT } from "../../../../../../common/src/constants";

const getStreamLanguages = (stream) => ['left', 'right'].map(channel => {
    const _language = stream.language?.find(sLang => sLang.channel === channel);
    return _language || defaultLanguages(stream.id)[channel];
})

export default ({ editingStream, setEditingStream, isSaving, liveProfiles, recordProfiles }) => {
    const { userIsAdmin } = useUserContext();

    const [languages, setLanguages] = useState([]);
    const [languagesModal, setLanguagesModal] = useState(false);
    const [streamLanguages, setStreamLanguages] = useState(getStreamLanguages(editingStream));

    const liveProfile = useMemo(() => liveProfiles?.find(({ id }) => id === editingStream.liveProfileId), [liveProfiles, editingStream.liveProfileId])
    const liveProfilesOptions = useMemo(() => liveProfiles?.map(profile => ({ value: profile.id, label: profile.name })), [liveProfiles]);
    const recordProfilesOptions = useMemo(() => recordProfiles?.map(profile => ({ value: profile.id, label: profile.name })), [recordProfiles]);
    const languagesOptions = useMemo(() =>
        languages?.map(language => ({
            value: String(language.code).toUpperCase(),
            label: String(language.code).toUpperCase() + " - " + language.labelRu
        })), [languages]);
    const streamQualities = editingStream?.quality || [];
    const enabledQualities = streamQualities.filter(({ enabled }) => enabled);
    const qualityPresetsOptions = useMemo(() => {
        const allQualitiesPresent = QUALITIES_DEFAULT.every((q) => streamQualities.find(({ height }) => height === q));
        return allQualitiesPresent
            ? [2, 3].map((idx) => ({
                value: QUALITIES_DEFAULT.slice(0, idx),
                label: QUALITIES_DEFAULT
                    .slice(0, idx)
                    .map((q) => q + 'p')
                    .join(', '),
            }))
            : [];
    }, [streamQualities]);

    const getLangs = () => {
        getLanguages()
            .then(languages => setLanguages(languages))
            .catch(e => console.error(e));
    }

    useEffect(() => {
        getLangs();
    }, []);

    useEffect(() => {
        setEditingStream(stream => ({ ...stream, language: streamLanguages }))
    }, [streamLanguages]);

    const handleTranscoder = (checked) => {
        const _editingStream = cloneDeep(editingStream);
        _editingStream.useTranscoder = checked;
        if (checked) {
            _editingStream.quality = _editingStream.quality.map(quality => {
                const { bitrate, width, height } = transcoderPresets[quality.number];
                return { ...quality, enabled: true, bitrate, width, height }
            })
        }

        setEditingStream(_editingStream);
    }

    const handleLanguage = (value, id) => {
        const _language = languages.find(lang => String(lang.code).toLowerCase() === String(value).toLowerCase());
        if (_language) {
            setStreamLanguages(languages => languages.map(language => language.id === id ? { ...language, label: value, description: _language.label } : language))
        }
    }

    return <div style={{ marginTop: '1rem' }}>
        {userIsAdmin && <>
            <FormGroup >
                <ControlLabel>CDN Live</ControlLabel>
                <Select
                    value={liveProfilesOptions.find(({ value }) => editingStream.liveProfileId === value)}
                    onChange={({ value }) => setEditingStream(stream => ({ ...stream, liveProfileId: value }))}
                    options={liveProfilesOptions}
                    isDisabled={isSaving}
                />
            </FormGroup>
            <FormGroup >
                <ControlLabel>CDN VoD</ControlLabel>
                <Select
                    value={recordProfilesOptions.find(({ value }) => editingStream.recordProfileId === value)}
                    onChange={({ value }) => setEditingStream(stream => ({ ...stream, recordProfileId: value }))}
                    options={recordProfilesOptions}
                    isDisabled={isSaving}
                />
            </FormGroup>
        </>}
        {editingStream.liveProfileId &&
            <FormGroup>
                <Checkbox
                    checked={editingStream.useTranscoder}
                    onChange={(e) => handleTranscoder(e.target.checked)}
                    disabled={isSaving}
                >Транскодер</Checkbox>
            </FormGroup>
        }
        {(liveProfile?.publishServer?.type === 'wowza' || editingStream.useTranscoder) &&
            <FormGroup >
                <ControlLabel>Потоки</ControlLabel>
                <Select
                    value={qualityPresetsOptions.find(({ value }) => enabledQualities.every(({ height }) => value.includes(height)))}
                    onChange={({ value }) => setEditingStream(stream => ({ ...stream, quality: streamQualities.map(q => ({...q, enabled: value.includes(q.height)})) }))}
                    options={qualityPresetsOptions}
                    isDisabled={isSaving}
                />
                {/* <QualitiesTable
                    qualities={editingStream.quality}
                    useTranscoder={editingStream.useTranscoder}
                    onChange={(quality) => setEditingStream(stream => ({ ...stream, quality }))}
                    isSaving={isSaving}
                    liveProfile={liveProfile}
                /> */}
            </FormGroup>
        }
        <FormGroup >
            <ControlLabel>Языки</ControlLabel>
            {userIsAdmin &&
                <div style={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
                    <Button onClick={() => setLanguagesModal(true)}>Список языков</Button>
                </div>
            }
            {streamLanguages.map(lang => {
                return <div key={lang.id || lang.label} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Checkbox
                        style={{ width: '7rem' }}
                        checked={lang.enabled}
                        onChange={(e) => setStreamLanguages(languages => languages.map(language => ({ ...language, enabled: e.target.checked })))}
                        disabled={isSaving}
                    >{lang.channel === 'left' ? 'Левый' : 'Правый'}</Checkbox>
                    <Select
                        value={languagesOptions.find(({ value }) => lang.label === value)}
                        onChange={({ value }) => handleLanguage(value, lang.id)}
                        options={languagesOptions}
                        isDisabled={isSaving}
                    />
                </div>
            })}
        </FormGroup>
        <FormGroup>
            <Checkbox
                checked={editingStream.manualStatusControl}
                onChange={(e) => setEditingStream(stream => ({ ...stream, manualStatusControl: e.target.checked }))}
                disabled={isSaving}
            >Ручное управление</Checkbox>
        </FormGroup>
        <FormGroup>
            <Checkbox
                checked={editingStream.dvr}
                onChange={(e) => setEditingStream(stream => ({ ...stream, dvr: e.target.checked }))}
                disabled={isSaving}
            >Воспроизводить запись</Checkbox>
        </FormGroup>
        <Modal
            show={languagesModal}
            onHide={() => setLanguagesModal(false)}
        >
            <Modal.Header closeButton>
                <Modal.Title>Список языков трансляций</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <LanguagesTable languages={languages} setLanguages={setLanguages} />
            </Modal.Body>
        </Modal>
    </div>
}

const QualitiesTable = ({ qualities, onChange, useTranscoder, isSaving, liveProfile }) => {
    const disabledSettings = liveProfile?.publishServer?.type === 'wowza' && useTranscoder;

    const handleChange = (key, value, id) => {
        onChange(qualities.map(quality => quality.number === id ? { ...quality, [key]: value } : quality))
    }

    const handleChangeDefault = (checked, number) => {
        onChange(qualities.map(quality => ({ ...quality, default: quality.number === number ? checked : !checked })))
    }

    return <Table>
        <thead>
            <tr>
                <th style={{ width: 50 }}>Вкл.</th>
                <th style={{ width: 150 }}>Битрейт</th>
                <th style={{ width: 70 }}>Ширина</th>
                <th style={{ width: 70 }}>Высота</th>
                <th style={{ width: 70 }}>Текст</th>
                <th style={{ width: 50 }}></th>
            </tr>
        </thead>
        <tbody>
            {qualities
                .map((quality) => (
                    <tr key={quality.number}>
                        <td>
                            <Checkbox
                                checked={quality.enabled}
                                onChange={(e) => handleChange('enabled', e.target.checked, quality.number)}
                                disabled={isSaving || disabledSettings}
                            />
                        </td>
                        <td>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', width: '100%' }}>
                                <Select
                                    value={bitrateOptions.find(({ value }) => quality.bitrate === value)}
                                    onChange={({ value }) => handleChange('bitrate', value, quality.number)}
                                    options={bitrateOptions}
                                    isDisabled={isSaving || disabledSettings}
                                />
                                <span style={{ flexShrink: 0 }}>Kbps</span>
                            </div>
                        </td>
                        <td>
                            <FormControl
                                type="number"
                                value={quality.width}
                                onChange={(e) => handleChange('width', e.target.value, quality.number)}
                                disabled={isSaving || disabledSettings}
                            />
                        </td>
                        <td>
                            <FormControl
                                type="number"
                                value={quality.height}
                                onChange={(e) => handleChange('height', e.target.value, quality.number)}
                                disabled={isSaving || disabledSettings}
                            />
                        </td>
                        <td>
                            <FormControl
                                type="text"
                                value={quality.label}
                                onChange={(e) => handleChange('label', e.target.value, quality.number)}
                            />
                        </td>
                        <td>
                            <Radio
                                id={quality.number}
                                name="default"
                                checked={quality.default}
                                onChange={(e) => handleChangeDefault(e.target.checked, quality.number)}
                                disabled={isSaving}
                            />
                        </td>
                    </tr>
                )
                )}
        </tbody>
    </Table>
}

const LanguagesTable = ({ languages, setLanguages }) => {
    const [editingLanguage, setEditingLanguage] = useState(null);

    const handleAddLanguage = () => {
        setEditingLanguage({
            code: '',
            label: '',
            labelRu: '',
        });
    }

    const handleDeleteLanguage = (language) => {
        deleteLanguage(language.id)
            .then(() => setLanguages(languages => languages.filter(({ id }) => id !== language.id)))
            .catch(e => console.error(e))
    }

    const handleSaveEditing = async () => {
        if (!editingLanguage) return;

        try {
            const response = await (editingLanguage.id ? updateLanguage(editingLanguage.id, editingLanguage) : addLanguage(editingLanguage));
            let _languages = cloneDeep(languages);
            if (editingLanguage.id) {
                _languages = _languages.map(language => language.id === editingLanguage.id ? { ...language, ...response } : language)
            } else {
                _languages.push(response)
            }
            setLanguages(_languages);
            setEditingLanguage(null);
        } catch (e) {
            console.error(e)
        }
    }

    return <div>
        <Table>
            <thead>
                <tr>
                    <th style={{ width: 30 }}>ID</th>
                    <th style={{ width: 50 }}>Код</th>
                    <th>Название</th>
                    <th>Название (RU)</th>
                    <th style={{ width: 100 }}></th>
                </tr>
            </thead>
            <tbody>
                {languages
                    .map((language) => (
                        <tr key={language.id}>
                            <td>{language.id}</td>
                            <td>{language.code}</td>
                            <td>{language.label}</td>
                            <td>{language.labelRu}</td>
                            <td>
                                <ButtonGroup>
                                    <Button onClick={() => setEditingLanguage(language)}>
                                        <Glyphicon glyph="edit" />
                                    </Button>
                                    <DeleteConfirmOverlay title="Удалить этот язык?" buttonText="Удалить" onConfirm={() => handleDeleteLanguage(language)}>
                                        <Button bsStyle="danger"><Glyphicon glyph="trash" /></Button>
                                    </DeleteConfirmOverlay>
                                </ButtonGroup>
                            </td>
                        </tr>
                    ))}
            </tbody>
        </Table>
        <div>
            <Button onClick={handleAddLanguage}>Добавить</Button>
        </div>
        <Modal
            show={!!editingLanguage}
            onHide={() => setEditingLanguage(null)}
        >
            <Modal.Header closeButton>
                <Modal.Title>Язык трансляции</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {editingLanguage?.id &&
                    <FormGroup>
                        <ControlLabel>ID</ControlLabel>
                        <FormControl
                            type="text"
                            value={editingLanguage?.id}
                            readOnly
                        />
                    </FormGroup>
                }
                <FormGroup>
                    <ControlLabel>Код</ControlLabel>
                    <FormControl
                        type="text"
                        value={editingLanguage?.code}
                        onChange={(e) => setEditingLanguage(language => ({ ...language, code: e.target.value }))}
                    />
                </FormGroup>
                <FormGroup>
                    <ControlLabel>Название</ControlLabel>
                    <FormControl
                        type="text"
                        value={editingLanguage?.label}
                        onChange={(e) => setEditingLanguage(language => ({ ...language, label: e.target.value }))}
                    />
                </FormGroup>
                <FormGroup>
                    <ControlLabel>Название (RU)</ControlLabel>
                    <FormControl
                        type="text"
                        value={editingLanguage?.labelRu}
                        onChange={(e) => setEditingLanguage(language => ({ ...language, labelRu: e.target.value }))}
                    />
                </FormGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle="success" onClick={handleSaveEditing}>Сохранить</Button>
            </Modal.Footer>
        </Modal>
    </div>
}
